import { debugLogger } from '../utils/debugLogger';

export interface WebRTCConfig {
  iceServers?: RTCIceServer[];
  videoConstraints?: MediaTrackConstraints;
  audioConstraints?: MediaTrackConstraints;
}

export interface SignalingMessage {
  type: 'offer' | 'answer' | 'ice-candidate' | 'join' | 'leave';
  data: any;
  senderId?: string;
  targetId?: string;
}

const DEFAULT_ICE_SERVERS: RTCIceServer[] = [
  { urls: 'stun:stun.l.google.com:19302' },
  { urls: 'stun:stun1.l.google.com:19302' },
  { urls: 'stun:stun2.l.google.com:19302' },
];

const DEFAULT_VIDEO_CONSTRAINTS: MediaTrackConstraints = {
  width: { ideal: 1280, max: 1920 },
  height: { ideal: 720, max: 1080 },
  frameRate: { ideal: 30, max: 60 }
};

const DEFAULT_AUDIO_CONSTRAINTS: MediaTrackConstraints = {
  echoCancellation: true,
  noiseSuppression: true,
  autoGainControl: true
};

export class WebRTCService {
  private peerConnections: Map<string, RTCPeerConnection> = new Map();
  private localStream: MediaStream | null = null;
  private config: WebRTCConfig;

  constructor(config: WebRTCConfig = {}) {
    this.config = {
      iceServers: config.iceServers || DEFAULT_ICE_SERVERS,
      videoConstraints: config.videoConstraints || DEFAULT_VIDEO_CONSTRAINTS,
      audioConstraints: config.audioConstraints || DEFAULT_AUDIO_CONSTRAINTS
    };
  }

  // Get user media
  async getUserMedia(video: boolean = true, audio: boolean = true): Promise<MediaStream> {
    try {
      const constraints: MediaStreamConstraints = {
        video: video ? this.config.videoConstraints : false,
        audio: audio ? this.config.audioConstraints : false
      };

      this.localStream = await navigator.mediaDevices.getUserMedia(constraints);
      debugLogger.log('WebRTCService', 'Got user media', {
        videoTracks: this.localStream.getVideoTracks().length,
        audioTracks: this.localStream.getAudioTracks().length
      });

      return this.localStream;
    } catch (error) {
      debugLogger.log('WebRTCService', 'Failed to get user media', error);
      throw new Error('Failed to access camera/microphone');
    }
  }

  // Get display media (screen sharing)
  async getDisplayMedia(): Promise<MediaStream> {
    try {
      const stream = await navigator.mediaDevices.getDisplayMedia({
        video: {
          cursor: 'always',
          displaySurface: 'monitor'
        } as any,
        audio: false
      });

      debugLogger.log('WebRTCService', 'Got display media', {
        videoTracks: stream.getVideoTracks().length
      });

      return stream;
    } catch (error) {
      debugLogger.log('WebRTCService', 'Failed to get display media', error);
      throw new Error('Failed to access screen');
    }
  }

  // Create peer connection
  createPeerConnection(
    peerId: string,
    onIceCandidate: (candidate: RTCIceCandidate) => void,
    onTrack: (stream: MediaStream) => void
  ): RTCPeerConnection {
    if (this.peerConnections.has(peerId)) {
      debugLogger.log('WebRTCService', 'Peer connection already exists', { peerId });
      return this.peerConnections.get(peerId)!;
    }

    const peerConnection = new RTCPeerConnection({
      iceServers: this.config.iceServers
    });

    // Handle ICE candidates
    peerConnection.onicecandidate = (event) => {
      if (event.candidate) {
        debugLogger.log('WebRTCService', 'ICE candidate generated', {
          peerId,
          candidate: event.candidate.candidate
        });
        onIceCandidate(event.candidate);
      }
    };

    // Handle incoming tracks
    peerConnection.ontrack = (event) => {
      debugLogger.log('WebRTCService', 'Received remote track', {
        peerId,
        kind: event.track.kind
      });
      onTrack(event.streams[0]);
    };

    // Handle connection state changes
    peerConnection.onconnectionstatechange = () => {
      debugLogger.log('WebRTCService', 'Connection state changed', {
        peerId,
        state: peerConnection.connectionState
      });
    };

    // Handle ICE connection state changes
    peerConnection.oniceconnectionstatechange = () => {
      debugLogger.log('WebRTCService', 'ICE connection state changed', {
        peerId,
        state: peerConnection.iceConnectionState
      });
    };

    // Add local tracks to peer connection
    if (this.localStream) {
      this.localStream.getTracks().forEach(track => {
        peerConnection.addTrack(track, this.localStream!);
      });
      debugLogger.log('WebRTCService', 'Added local tracks to peer connection', { peerId });
    }

    this.peerConnections.set(peerId, peerConnection);
    return peerConnection;
  }

  // Create offer
  async createOffer(peerId: string): Promise<RTCSessionDescriptionInit> {
    const peerConnection = this.peerConnections.get(peerId);
    if (!peerConnection) {
      throw new Error(`Peer connection not found for ${peerId}`);
    }

    try {
      const offer = await peerConnection.createOffer();
      await peerConnection.setLocalDescription(offer);
      
      debugLogger.log('WebRTCService', 'Created offer', {
        peerId,
        sdp: offer.sdp?.substring(0, 100) + '...'
      });

      return offer;
    } catch (error) {
      debugLogger.log('WebRTCService', 'Failed to create offer', error);
      throw error;
    }
  }

  // Create answer
  async createAnswer(peerId: string): Promise<RTCSessionDescriptionInit> {
    const peerConnection = this.peerConnections.get(peerId);
    if (!peerConnection) {
      throw new Error(`Peer connection not found for ${peerId}`);
    }

    try {
      const answer = await peerConnection.createAnswer();
      await peerConnection.setLocalDescription(answer);
      
      debugLogger.log('WebRTCService', 'Created answer', {
        peerId,
        sdp: answer.sdp?.substring(0, 100) + '...'
      });

      return answer;
    } catch (error) {
      debugLogger.log('WebRTCService', 'Failed to create answer', error);
      throw error;
    }
  }

  // Set remote description
  async setRemoteDescription(peerId: string, description: RTCSessionDescriptionInit): Promise<void> {
    const peerConnection = this.peerConnections.get(peerId);
    if (!peerConnection) {
      throw new Error(`Peer connection not found for ${peerId}`);
    }

    try {
      await peerConnection.setRemoteDescription(new RTCSessionDescription(description));
      debugLogger.log('WebRTCService', 'Set remote description', { peerId, type: description.type });
    } catch (error) {
      debugLogger.log('WebRTCService', 'Failed to set remote description', error);
      throw error;
    }
  }

  // Add ICE candidate
  async addIceCandidate(peerId: string, candidate: RTCIceCandidateInit): Promise<void> {
    const peerConnection = this.peerConnections.get(peerId);
    if (!peerConnection) {
      throw new Error(`Peer connection not found for ${peerId}`);
    }

    try {
      await peerConnection.addIceCandidate(new RTCIceCandidate(candidate));
      debugLogger.log('WebRTCService', 'Added ICE candidate', {
        peerId,
        candidate: candidate.candidate
      });
    } catch (error) {
      debugLogger.log('WebRTCService', 'Failed to add ICE candidate', error);
      throw error;
    }
  }

  // Toggle audio
  toggleAudio(enabled?: boolean): boolean {
    if (!this.localStream) return false;

    const audioTracks = this.localStream.getAudioTracks();
    const newState = enabled !== undefined ? enabled : !audioTracks[0]?.enabled;

    audioTracks.forEach(track => {
      track.enabled = newState;
    });

    debugLogger.log('WebRTCService', 'Toggled audio', { enabled: newState });
    return newState;
  }

  // Toggle video
  toggleVideo(enabled?: boolean): boolean {
    if (!this.localStream) return false;

    const videoTracks = this.localStream.getVideoTracks();
    const newState = enabled !== undefined ? enabled : !videoTracks[0]?.enabled;

    videoTracks.forEach(track => {
      track.enabled = newState;
    });

    debugLogger.log('WebRTCService', 'Toggled video', { enabled: newState });
    return newState;
  }

  // Replace video track (for screen sharing)
  async replaceVideoTrack(newTrack: MediaStreamTrack): Promise<void> {
    const promises: Promise<void>[] = [];

    this.peerConnections.forEach((peerConnection, peerId) => {
      const sender = peerConnection.getSenders().find(s => s.track?.kind === 'video');
      if (sender) {
        promises.push(
          sender.replaceTrack(newTrack).then(() => {
            debugLogger.log('WebRTCService', 'Replaced video track', { peerId });
          })
        );
      }
    });

    await Promise.all(promises);
  }

  // Close peer connection
  closePeerConnection(peerId: string): void {
    const peerConnection = this.peerConnections.get(peerId);
    if (peerConnection) {
      peerConnection.close();
      this.peerConnections.delete(peerId);
      debugLogger.log('WebRTCService', 'Closed peer connection', { peerId });
    }
  }

  // Close all peer connections
  closeAllPeerConnections(): void {
    this.peerConnections.forEach((_, peerId) => {
      this.closePeerConnection(peerId);
    });
    debugLogger.log('WebRTCService', 'Closed all peer connections');
  }

  // Stop local stream
  stopLocalStream(): void {
    if (this.localStream) {
      this.localStream.getTracks().forEach(track => track.stop());
      this.localStream = null;
      debugLogger.log('WebRTCService', 'Stopped local stream');
    }
  }

  // Cleanup
  cleanup(): void {
    this.closeAllPeerConnections();
    this.stopLocalStream();
    debugLogger.log('WebRTCService', 'Cleaned up WebRTC service');
  }

  // Get local stream
  getLocalStream(): MediaStream | null {
    return this.localStream;
  }

  // Get peer connection
  getPeerConnection(peerId: string): RTCPeerConnection | undefined {
    return this.peerConnections.get(peerId);
  }

  // Get all peer IDs
  getPeerIds(): string[] {
    return Array.from(this.peerConnections.keys());
  }

  // Get connection stats
  async getStats(peerId: string): Promise<RTCStatsReport | null> {
    const peerConnection = this.peerConnections.get(peerId);
    if (!peerConnection) return null;

    try {
      return await peerConnection.getStats();
    } catch (error) {
      debugLogger.log('WebRTCService', 'Failed to get stats', error);
      return null;
    }
  }
}

export const webrtcService = new WebRTCService();
